import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import { stats } from '@/mocks/homepage';
import { aboutInfo, testimonials, milestones, contactInfo } from '@/mocks/about';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const organizationJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: aboutInfo.brandName,
    url: 'https://www.example.com',
    description: aboutInfo.introduction,
    email: contactInfo.email,
    address: {
      '@type': 'PostalAddress',
      streetAddress: contactInfo.address,
      addressLocality: '北京',
      addressCountry: 'CN',
    },
  };

  const aboutPageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `关于我们 - ${aboutInfo.brandName}`,
    description: aboutInfo.introduction,
    url: 'https://www.example.com/about',
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotField = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotField && honeypotField.value.trim() !== '') {
      setFormStatus('success');
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const formData = new FormData(form);
      formData.delete('phone_alt');
      const response = await fetch('https://readdy.ai/api/form/d9s94vue3oq4jqumu7e0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = {}; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormError(serverMsg || '提交失败，请稍后重试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`关于我们 - ${aboutInfo.brandName}`}
        description={aboutInfo.introduction}
        keywords="美女视频,关于我们,品牌介绍,联系方式,视频平台"
        canonicalUrl="https://www.example.com/about"
        jsonLd={[organizationJsonLd, aboutPageJsonLd]}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="w-full px-4 md:px-6 bg-background-100/70 border-b border-background-200/60">
            <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
          </div>

          <section className="py-14 md:py-20 bg-background-50">
            <div className="w-full px-4 md:px-6 max-w-4xl mx-auto text-center">
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4">
                关于我们
              </h1>
              <p className="text-lg text-primary-600 font-heading mb-6">{aboutInfo.tagline}</p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-3xl mx-auto">
                {aboutInfo.introduction}
              </p>
            </div>
          </section>

          <section className="py-10 bg-background-50">
            <div className="w-full px-4 md:px-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
                {stats.map((stat) => (
                  <div key={stat.label} className="flex flex-col items-center text-center p-4">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-3">
                      <i className={`${stat.icon} text-xl`}></i>
                    </div>
                    <div className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-1">{stat.value}</div>
                    <div className="text-sm text-foreground-500">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20 bg-background-100/70">
            <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                    我们的使命
                  </h2>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-6">{aboutInfo.mission}</p>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                    我们的愿景
                  </h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">{aboutInfo.vision}</p>
                </div>
                <div className="bg-background-50 rounded-xl p-6 border border-background-200/60">
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-5">发展历程</h3>
                  <div className="space-y-4">
                    {milestones.map((m, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <div className="flex flex-col items-center">
                          <div className="w-3 h-3 rounded-full bg-primary-500 flex-shrink-0 mt-1"></div>
                          {i < milestones.length - 1 && <div className="w-0.5 h-6 bg-primary-200"></div>}
                        </div>
                        <div>
                          <span className="text-xs font-semibold text-primary-600">{m.year}</span>
                          <p className="text-sm text-foreground-700 mt-0.5">{m.event}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20 bg-background-50">
            <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                  用户口碑
                </h2>
                <p className="text-sm text-foreground-500">听听用户们怎么说</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {testimonials.map((t, i) => (
                  <div key={i} className="bg-background-100/70 rounded-xl p-6 border border-background-200/60">
                    <div className="flex items-center gap-3 mb-4">
                      <img
                        src={t.avatar}
                        alt={t.name}
                        className="w-12 h-12 rounded-full object-cover"
                        title={t.name}
                      />
                      <div>
                        <div className="text-sm font-semibold text-foreground-900">{t.name}</div>
                        <div className="text-xs text-foreground-500">{t.role}</div>
                      </div>
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-3">&ldquo;{t.content}&rdquo;</p>
                    <div className="flex items-center gap-0.5">
                      {Array.from({ length: t.rating }).map((_, j) => (
                        <i key={j} className="ri-star-fill text-accent-500 text-sm"></i>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section id="contact" className="py-14 md:py-20 bg-background-100/70">
            <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                    联系我们
                  </h2>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-8">
                    如果您有任何问题、建议或合作意向，欢迎通过以下方式联系我们，或填写右侧表单，我们会尽快回复您。
                  </p>
                  <div className="space-y-5">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                        <i className="ri-mail-line text-lg"></i>
                      </div>
                      <div>
                        <div className="text-xs text-foreground-500">邮箱</div>
                        <div className="text-sm font-medium text-foreground-900">{contactInfo.email}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                        <i className="ri-wechat-line text-lg"></i>
                      </div>
                      <div>
                        <div className="text-xs text-foreground-500">微信</div>
                        <div className="text-sm font-medium text-foreground-900">{contactInfo.wechat}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                        <i className="ri-weibo-line text-lg"></i>
                      </div>
                      <div>
                        <div className="text-xs text-foreground-500">微博</div>
                        <div className="text-sm font-medium text-foreground-900">{contactInfo.weibo}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                        <i className="ri-map-pin-line text-lg"></i>
                      </div>
                      <div>
                        <div className="text-xs text-foreground-500">地址</div>
                        <div className="text-sm font-medium text-foreground-900">{contactInfo.address}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-background-50 rounded-xl p-6 md:p-8 border border-background-200/60">
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-6">发送消息</h3>
                  <form data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                    <input
                      type="text"
                      name="phone_alt"
                      className="honeypot-field"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-white text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-white text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                        placeholder="请输入您的邮箱地址"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                      <input
                        id="contact-subject"
                        name="subject"
                        type="text"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-white text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                        placeholder="请输入消息主题"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">消息内容</label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-white text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all resize-none"
                        placeholder="请输入您的消息内容（最多500字）"
                      ></textarea>
                    </div>

                    {formStatus === 'success' && (
                      <div className="p-3 rounded-lg bg-green-50 border border-green-200 text-sm text-green-700">
                        <i className="ri-check-line mr-1"></i>
                        消息已发送成功！我们会尽快回复您。
                      </div>
                    )}
                    {formStatus === 'error' && (
                      <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
                        <i className="ri-error-warning-line mr-1"></i>
                        {formError || '发送失败，请稍后重试'}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={formStatus === 'loading'}
                      className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-lg font-medium text-sm whitespace-nowrap transition-all duration-200 disabled:opacity-60 cursor-pointer"
                    >
                      {formStatus === 'loading' ? (
                        <>
                          <i className="ri-loader-4-line animate-spin mr-2"></i>
                          发送中...
                        </>
                      ) : (
                        '发送消息'
                      )}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}